import React, { useEffect, useState } from "react";
import { useHistory } from "react-router";
import { Switch } from "../../../UI/Switch";
import styled from "@emotion/styled";
import { COLOR_MISCHKA, COLOR_STALE_GRAY } from "../../../constants/styles";
import { Table } from "../../../UI/Table";
import { TableHead } from "../../../UI/TableHead";
import { TableRow } from "../../../UI/TableRow";
import { TableCell } from "../../../UI/TableCell";
import { Button } from "../../../UI/Button";
import { ModalProvider } from "../../../contexts/ModalContext";
import { useModalContext } from "../../../contexts/ModalContext/context";
import { NetworkIntegrationEditModal } from "./NetworkIntegrationEditModal";
import { PORTFOLIO_NETWORK_DETAILED } from "./PortfolioNetworkDetails";
import { StyledTableRow } from './sharedElements';
import useAuth from "../../login/AuthContext";
import { useSnackbar } from "../../../hooks/useSnackbar";
import {
  LoadPortfolioWithImportSettings,
  PortfolioWithNetworks,
  NetworkWithImportSettings,
  LoadPortfolioIntegrations,
  PorfolioIntegraion,
  UpdateNetworkImportSettings
} from "../../../services/PortfolioApi";
import { handleNavigation } from "../../../helpers/handleNavigation";

const StyledSwitch = styled(Switch)`
  opacity: ${props => props.disabled ? '0.5' : '1'};

  .MuiSwitch-switchBase.Mui-disabled {
    color: ${COLOR_STALE_GRAY} !important;

    & + .MuiSwitch-track {
      background-color: ${COLOR_MISCHKA} !important;
      opacity: 1 !important;
    }

    & .MuiSwitch-thumb {
      background-color: ${COLOR_STALE_GRAY} !important;
    }
  }

  .MuiSwitch-switchBase.Mui-disabled .MuiSwitch-thumb {
    background-color: ${COLOR_STALE_GRAY} !important;
  }
`;

interface CommunitiesTabProps {
  portfolioId: string;
}

const COLUMN_WIDTHS = {
  networkName: 250,
  integrationType: 200,
  networkId: 150,
  externalPropertyId: 200,
  lastImportRun: 200,
  importEnabled: 120,
  actions: 100,
} as const;

// Helper function to format the lastRun date array
const formatLastRun = (lastRun: number[] | null): string => {
  if (!lastRun || lastRun.length < 6) {
    return '-';
  }

  const [year, month, day, hour, minute, second] = lastRun;
  const date = new Date(year, month - 1, day, hour, minute, second);
  return date.toLocaleString();
};

// Helper function to get integration type from portfolioImportSettingsId
const getIntegrationType = (portfolioImportSettingsId: number | undefined, portfolioIntegrations: PorfolioIntegraion[]): string => {
  if (!portfolioImportSettingsId || !portfolioIntegrations) {
    return 'Unknown';
  }

  const integration = portfolioIntegrations.find(integration => integration.id === portfolioImportSettingsId);
  return integration?.importType || 'Unknown';
};

export const CommunitiesTab: React.FC<CommunitiesTabProps> = ({ portfolioId }) => {
  const [portfolio, setPortfolio] = useState<PortfolioWithNetworks | null>(null);
  const [portfolioIntegrations, setPortfolioIntegrations] = useState<PorfolioIntegraion[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedNetwork, setSelectedNetwork] = useState<NetworkWithImportSettings | null>(null);
  const { getAuthToken } = useAuth();

  const loadPortfolioData = async () => {
    try {
      setIsLoading(true);
      const token = await getAuthToken();

      // Load both portfolio data and integrations
      const [portfolioData, integrationsData] = await Promise.all([
        LoadPortfolioWithImportSettings(portfolioId, token),
        LoadPortfolioIntegrations(portfolioId, token)
      ]);

      if (portfolioData) {
        setPortfolio(portfolioData);
      }

      if (integrationsData?.settings) {
        setPortfolioIntegrations(integrationsData.settings);
      }
    } catch (error) {
      // console.error('Failed to load portfolio data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadPortfolioData();
  }, [portfolioId]);

  if (isLoading) {
    return <>Loading...</>;
  }

  if (!portfolio) {
    return <>Failed to load portfolio data</>;
  }

  return (
    <ModalProvider>
      <ModalContent
        portfolio={portfolio}
        portfolioIntegrations={portfolioIntegrations}
        selectedNetwork={selectedNetwork}
        setSelectedNetwork={setSelectedNetwork}
        portfolioId={portfolioId}
        onRefresh={loadPortfolioData}
      />
    </ModalProvider>
  );
};

// Create a new component to use the modal context
const ModalContent: React.FC<{
  portfolio: PortfolioWithNetworks;
  portfolioIntegrations: PorfolioIntegraion[];
  selectedNetwork: NetworkWithImportSettings | null;
  setSelectedNetwork: (network: NetworkWithImportSettings | null) => void;
  portfolioId: string;
  onRefresh: () => Promise<void>;
}> = ({ portfolio, portfolioIntegrations, selectedNetwork, setSelectedNetwork, portfolioId, onRefresh }) => {
  const { setIsOpen } = useModalContext();
  const { push } = useHistory();

  const handleEditClick = (network: NetworkWithImportSettings) => {
    setSelectedNetwork(network);
    setIsOpen(true);
  };

  const handleRowClick = (network: NetworkWithImportSettings) => (event: React.MouseEvent) => {
    handleNavigation(
      event,
      PORTFOLIO_NETWORK_DETAILED.replace(":portfolioId", portfolioId).replace(":networkId", String(network.l1Id)),
      push
    );
  };


  return (
    <>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell width={COLUMN_WIDTHS.networkName}>Network Name</TableCell>
            <TableCell width={COLUMN_WIDTHS.integrationType}>Integration Type</TableCell>
            <TableCell width={COLUMN_WIDTHS.networkId}>Network ID</TableCell>
            <TableCell width={COLUMN_WIDTHS.externalPropertyId}>External Property ID</TableCell>
            <TableCell width={COLUMN_WIDTHS.lastImportRun}>Last Import Run</TableCell>
            <TableCell width={COLUMN_WIDTHS.importEnabled}>Import Enabled</TableCell>
            <TableCell width={COLUMN_WIDTHS.actions} align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        {portfolio.networks.map(network => (
          <NetworkRow
            key={network.l1Id}
            network={network}
            portfolioIntegrations={portfolioIntegrations}
            onEdit={() => handleEditClick(network)}
            onRowClick={handleRowClick(network)}
            portfolioId={portfolioId}
            onRefresh={onRefresh}
          />
        ))}
      </Table>

      {selectedNetwork && (() => {
        const selectedIntegrationType = getIntegrationType(
          selectedNetwork.importSettings?.portfolioImportSettingsId,
          portfolioIntegrations
        );
        const isConnect = !(
          selectedNetwork.importSettings?.portfolioImportSettingsId &&
          selectedIntegrationType !== 'Unknown'
        );
        return (
          <NetworkIntegrationEditModal
            network={{
              l1Id: selectedNetwork.l1Id,
              integrationType: selectedIntegrationType,
            }}
            portfolioId={portfolioId}
            isConnect={isConnect}
            onClose={() => {
              setSelectedNetwork(null);
              setIsOpen(false);
            }}
            onRefresh={onRefresh}
          />
        );
      })()}
    </>
  );
};

interface NetworkRowProps {
  network: NetworkWithImportSettings;
  portfolioIntegrations: PorfolioIntegraion[];
  onEdit: () => void;
  onRowClick: (event: React.MouseEvent) => void;
  portfolioId: string;
  onRefresh: () => Promise<void>;
}

const NetworkRow: React.FC<NetworkRowProps> = ({ network, portfolioIntegrations, onEdit, onRowClick, portfolioId, onRefresh }) => {
  const integrationType = getIntegrationType(network.importSettings?.portfolioImportSettingsId, portfolioIntegrations);
  const externalPropertyId = network.importSettings?.externalIds.join(', ') || '-';
  const lastImportRun = formatLastRun(network.importSettings?.lastRun || null);
  const [importEnabled, setImportEnabled] = useState(network.importSettings?.enabled || false);
  const { getAuthToken } = useAuth();
  const { showErrorSnackbar, showSuccessSnackbar } = useSnackbar();

  // Determine if this is a connection (no integration type) or migration
  const hasIntegrationType = network.importSettings?.portfolioImportSettingsId && integrationType !== 'Unknown';
  const buttonText = hasIntegrationType ? 'Migrate' : 'Connect';

  // Update local state when network data changes
  useEffect(() => {
    setImportEnabled(network.importSettings?.enabled || false);
  }, [network.importSettings?.enabled]);

  const handleSwitchChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
    event.stopPropagation();

    const newEnabled = event.target.checked;
    const networkSettingsId = network.importSettings?.id;

    if (!networkSettingsId) {
      showErrorSnackbar('Unable to update import settings: missing configuration');
      return;
    }

    try {
      const token = await getAuthToken();
      const result = await UpdateNetworkImportSettings(
        portfolioId,
        network.l1Id,
        networkSettingsId,
        newEnabled,
        token
      );

      if (result.ok) {
        setImportEnabled(newEnabled); // Update local state to reflect the change
        showSuccessSnackbar(`Import ${newEnabled ? 'enabled' : 'disabled'} successfully`);
      } else {
        showErrorSnackbar('Failed to update import settings');
      }
    } catch (error) {
      showErrorSnackbar('Failed to update import settings');
    }
  };

  return (
    <StyledTableRow onClick={onRowClick}>
      <TableCell width={COLUMN_WIDTHS.networkName}>{network.name}</TableCell>
      <TableCell width={COLUMN_WIDTHS.integrationType}>{integrationType}</TableCell>
      <TableCell width={COLUMN_WIDTHS.networkId}>{network.l1Id}</TableCell>
      <TableCell width={COLUMN_WIDTHS.externalPropertyId}>{externalPropertyId}</TableCell>
      <TableCell width={COLUMN_WIDTHS.lastImportRun}>{lastImportRun}</TableCell>
      <TableCell width={COLUMN_WIDTHS.importEnabled} align="right">
        <StyledSwitch
          checked={importEnabled}
          onChange={handleSwitchChange}
          onClick={(e) => e.stopPropagation()} // Prevent row click when clicking the switch
        />
      </TableCell>
      <TableCell width={COLUMN_WIDTHS.actions} align="right">
        <Button
          onClick={(e) => {
            e.stopPropagation(); // Prevent row click when clicking the button
            onEdit();
          }}
        >
          {buttonText}
        </Button>
      </TableCell>
    </StyledTableRow>
  );
};
